import { zodResolver } from "@hookform/resolvers/zod";
import {
  BMBadgeTag,
  BMInputField,
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
} from "@traveloka/district-bloom";
import IcSystemCalendar16 from "@traveloka/icon-kit-web/react/IcSystemCalendar16";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import { PREBOOK_PASSAGERS_BACKDROP } from "../../../../../../assets/backdrop";
import {
  BMDateInputField,
  BMDropdownField,
  BMNationalityField,
} from "../../../../../../components";
import BMView from "../../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../../../utils";
import { BMDrawer } from "../../../../common";
import FlightBaggageField from "../../../../Flight/FlightBaggageField/FlightBaggageField";
import { HONORIFIC_GENDER_MAP } from "../../../constant";
import {
  FlightPassengerDocumentType,
  FlightPrebookAddOnsQuery,
  Honorific,
} from "../../../types";
import { FlightPrebookPassengerFormValue } from "../../types";
import BaggageCatalogError from "./BaggageCatalogError";
import { useBaggageInfos } from "./hooks/useBaggageInfos";
import { usePassengerSchema } from "./hooks/usePassengerSchema";

const BACKDROP_HEIGHT = 240;

type PassengerFormProps = {
  index: number;
  departureDate: Date;
  isTripDomesticID: boolean;
  addOns: FlightPrebookAddOnsQuery;
  defaultValues: FlightPrebookPassengerFormValue;
  onSave: (data: FlightPrebookPassengerFormValue) => void;
  onClose: () => void;
};

/**
 * PassengerForm
 * The form inside the drawer for editing passenger details.
 * Uses react-hook-form for form state management.
 */
export default function FlightPrebookPassengerDrawer({
  index,
  addOns,
  defaultValues,
  departureDate,
  isTripDomesticID,
  onSave,
  onClose,
}: PassengerFormProps) {
  const [visible, setVisible] = useState(true);
  const {
    CorporateEnumTravelerTitle: cr,
    CorporateFlightProductSummary: crs,
    CorporateFlightPrebookForm: crf,
  } = useMobileContentResource();

  const {
    data: addOnsData,
    isLoading: isLoadingBaggageAddOns,
    isError: isErrorBaggageAddOns,
    refetch: refetchBaggageAddOns,
  } = addOns;

  const isInfant = defaultValues.type === "INFANT";

  const passengerSchema = usePassengerSchema(
    defaultValues,
    departureDate,
    isTripDomesticID
  );

  const baggageInfos = useBaggageInfos({
    isInfant,
    defaultValues,
    catalogBaggageAddOns: addOnsData?.catalog || {},
  });

  const methods = useForm<FlightPrebookPassengerFormValue>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldUnregister: false,
    resolver: zodResolver(passengerSchema),
  });
  const { control, handleSubmit, getValues, setValue } = methods;

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  const handleSubmitForm = useCallback(
    (data: FlightPrebookPassengerFormValue) => {
      setVisible(false);
      setTimeout(() => onSave(data), 300);
    },
    [onSave]
  );

  const handleChangeNationality = useCallback(
    (nationality: string | null) => {
      setValue("nationality", nationality ?? "", { shouldValidate: true });

      const hasDoc = getValues("documentDetail");
      if (!hasDoc) return;

      const domesticLike =
        isTripDomesticID && (!nationality || nationality === "ID");
      if (!domesticLike) {
        setValue(
          "documentDetail.documentType",
          FlightPassengerDocumentType.PASSPORT
        );
        setValue("documentDetail.documentNo", "");
        setValue("documentDetail.issuingCountry", "");
        setValue("documentDetail.expirationDate", null);
      } else {
        setValue(
          "documentDetail.documentType",
          FlightPassengerDocumentType.NATIONAL_ID
        );
        setValue("documentDetail.documentNo", "");
        setValue("documentDetail.issuingCountry", "");
        setValue("documentDetail.expirationDate", null);
      }
    },
    [isTripDomesticID]
  );

  const handleChangeTitle = useCallback((title: string | null) => {
    if (!title) return;
    setValue("title", title as Honorific, { shouldValidate: true });
    setValue(
      "gender",
      HONORIFIC_GENDER_MAP[title as keyof typeof HONORIFIC_GENDER_MAP]
    );
  }, []);

  // Determine which fields to show based on form values based on defaultValues
  const {
    isShowFullNameField,
    isShowDateOfBirthField,
    isShowNationalityField,
  } = useMemo(() => {
    return {
      isShowFullNameField:
        getValues("type") !== "ADULT" || !getValues("isEmployee"),
      isShowDateOfBirthField: getValues("dateOfBirth") !== undefined,
      isShowNationalityField: getValues("nationality") !== undefined,
    };
  }, []);

  // Watch nationality to conditionally show document fields
  const nationality = useWatch({
    control,
    name: "nationality",
  });
  const isShowDocumentDetailField = useMemo(() => {
    const hasDoc = !!getValues("documentDetail");
    const domesticLike =
      isTripDomesticID && (!nationality || nationality === "ID");

    return hasDoc && !domesticLike;
  }, [isTripDomesticID, nationality]);

  const drawerTitle = useMemo(() => {
    let name = "";
    if (defaultValues.fullname) {
      name = defaultValues.fullname;
    } else {
      const group = crf[defaultValues.type];
      name = `${group} ${index + 1}`;
    }
    return formatMessage(crf.passengerFormDetailDrawerTitle, { name });
  }, [defaultValues.fullname, defaultValues.type, index]);

  const titleFieldOptions = useMemo(
    () => [
      { label: cr.MR, value: "MR" },
      { label: cr.MRS, value: "MRS" },
      { label: cr.MISS, value: "MISS" },
    ],
    [cr]
  );

  return (
    <FormProvider {...methods}>
      <BMDrawer
        show={visible}
        variant="COMPACT"
        headerNavigationProps={{
          children: <BMText variant="titleSmall">{drawerTitle}</BMText>,
          onBackActionPress: handleClose,
        }}
        footerButtonProps={{
          label: crf.saveFormCta,
          onPress: handleSubmit(handleSubmitForm),
        }}
        backdropProps={{
          source: { uri: PREBOOK_PASSAGERS_BACKDROP },
          style: { height: BACKDROP_HEIGHT },
        }}
      >
        <BMView style={styles.container} spacing="xl2">
          <BMView spacing="lg">
            <BMText variant="titleSmall">{crf.passengerFormDetailTitle}</BMText>
            <BMView row spacing="md">
              <BMView flex={isShowFullNameField ? 0.4 : 1}>
                <Controller
                  name={"title"}
                  control={control}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <BMDropdownField
                      value={value}
                      onChange={handleChangeTitle}
                      label={crf.passengerFormTitleFieldLabel}
                      placeholder={crf.passengerFormTitleFieldPlaceholder}
                      option={{ items: titleFieldOptions }}
                      error={error?.message}
                    />
                  )}
                />
              </BMView>
              {isShowFullNameField ? (
                <BMView flex>
                  <Controller
                    name={"fullname"}
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <BMInputField
                        label={crf.passengerFormFullnameFieldLabel}
                        placeholder={crf.passengerFormFullnameFieldPlaceholder}
                        value={value ?? ""}
                        onChangeText={onChange}
                        error={error?.message}
                      />
                    )}
                  />
                </BMView>
              ) : null}
            </BMView>
            {defaultValues.type === "ADULT" && !defaultValues.isEmployee ? (
              <BMView flex>
                <Controller
                  name={"email"}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <BMInputField
                      value={value ?? ""}
                      label={crf.passengerFormEmailFieldLabel}
                      placeholder={crf.passengerFormEmailFieldPlaceholder}
                      onChangeText={(v) => onChange(v ? v : undefined)}
                      error={error?.message}
                    />
                  )}
                />
              </BMView>
            ) : null}
            {isShowDateOfBirthField ? (
              <Controller
                name={"dateOfBirth"}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <BMDateInputField
                    value={value || null}
                    onChange={onChange}
                    LeadingIcon={IcSystemCalendar16}
                    error={error?.message}
                    label={crf.passengerFormDateOfBirthFieldLabel}
                  />
                )}
              />
            ) : null}
            {isShowNationalityField ? (
              <Controller
                name={"nationality"}
                control={control}
                render={({ field: { value }, fieldState: { error } }) => (
                  <BMNationalityField
                    value={value || null}
                    onChange={handleChangeNationality}
                    label={crf.passengerFormNationalityFieldLabel}
                    placeholder={crf.passengerFormNationalityFieldPlaceholder}
                    optionsSection={{
                      title: crf.passengerFormNationalityFieldLabel,
                    }}
                    error={error?.message}
                  />
                )}
              />
            ) : null}
            {isShowDocumentDetailField ? (
              <BMView spacing="lg">
                <Controller
                  name={"documentDetail.documentNo"}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <BMInputField
                      value={value ?? ""}
                      onChangeText={onChange}
                      label={crf.passengerFormPassportNumberFieldLabel}
                      placeholder={
                        crf.passengerFormPassportNumberFieldPlaceholder
                      }
                      error={error?.message}
                    />
                  )}
                />
                <Controller
                  name={"documentDetail.issuingCountry"}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <BMNationalityField
                      value={value || null}
                      onChange={onChange}
                      label={crf.passengerFormPassportCountryIssuanceFieldLabel}
                      placeholder={
                        crf.passengerFormPassportCountryIssuanceFieldPlaceholder
                      }
                      optionsSection={{
                        title:
                          crf.passengerFormPassportCountryIssuanceFieldLabel,
                      }}
                      error={error?.message}
                    />
                  )}
                />
                <Controller
                  name={"documentDetail.expirationDate"}
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <BMDateInputField
                      label={crf.passengerFormPassportExpiryDateFieldLabel}
                      value={value || null}
                      onChange={onChange}
                      LeadingIcon={IcSystemCalendar16}
                      error={error?.message}
                    />
                  )}
                />
              </BMView>
            ) : null}
          </BMView>
          {!isInfant ? (
            <BMView spacing="lg">
              <BMText variant="titleSmall">
                {crf.passengerFormFlightFacilitiesTitle}
              </BMText>
              {isLoadingBaggageAddOns ? (
                <BMView spacing="lg">
                  {[0, 1].map((idx) => (
                    <BMView key={`baggage-addons-loading-${idx}`} spacing="sm">
                      <BMView row spacing="sm">
                        <BMSkeletonLoader width={42} height={18} />
                        <BMSkeletonLoader width={136} height={18} />
                      </BMView>
                      <BMSkeletonLoader width={42} height={18} />
                      <BMSkeletonLoader width={"100%"} height={40} />
                      <BMSkeletonLoader width={42} height={18} />
                      <BMSkeletonLoader width={"100%"} height={40} />
                    </BMView>
                  ))}
                </BMView>
              ) : null}
              {!isLoadingBaggageAddOns && isErrorBaggageAddOns ? (
                <BaggageCatalogError onRetry={refetchBaggageAddOns} />
              ) : null}
              {!isLoadingBaggageAddOns &&
                !isErrorBaggageAddOns &&
                baggageInfos.map((info, idx) => (
                  <BMView
                    spacing="sm"
                    key={`baggage-options-group_${info.departureAirport}-${info.arrivalAirport}-${idx}`}
                  >
                    <BMView row spacing="sm" align="center">
                      <BMBadgeTag
                        type="GREEN"
                        label={idx === 0 ? crs.ENUM_DEPART : crs.ENUM_RETURN}
                      />
                      <BMText variant="uiNormal">
                        {info.departureAirport} → {info.arrivalAirport}
                      </BMText>
                    </BMView>
                    {info.segments.map((segment, jdx) => (
                      <Controller
                        control={control}
                        key={`baggage-field-${segment.id}-${jdx}`}
                        name={segment.name as any}
                        render={({ field: { value, onChange } }) => (
                          <FlightBaggageField
                            value={value}
                            zIndex={100 - jdx}
                            onChange={onChange}
                            options={segment.catalog}
                            label={`${segment.departureAirport.airportCode} - ${segment.arrivalAirport.airportCode}`}
                            getOptionValue={(v) => (v ? v.id : "")}
                          />
                        )}
                      />
                    ))}
                  </BMView>
                ))}
            </BMView>
          ) : null}
        </BMView>
      </BMDrawer>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: BMPrimitives.BMSize.lg,
    paddingHorizontal: BMPrimitives.BMSize.md,
  },
});
